/**
 * RTWO Event Bus — Routing Engine Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type {
  EventId,
  RouteId,
  RTWOEventRoute,
  RTWORouteStatus,
  RTWORoutingPolicy,
} from '../domain-models';
import type { IRoutingEngineService, RoutingResult } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class RoutingEngineService extends BaseEBusService implements IRoutingEngineService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.ROUTING_ENGINE;
  readonly serviceName = 'Routing Engine';
  readonly description =
    'Routes events from publishers to subscribers. No routes configured until real event sources connect.';
  readonly integrations = ['Publisher Registry', 'Subscriber Registry', 'Delivery Engine'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async routeEvent(eventId: EventId): Promise<RoutingResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        routeId: null,
        error:
          'Routing Engine Not Active — Cannot route events until real event sources are connected.',
        isPlaceholder: true,
        source: 'Event Bus — Routing Engine Not Active',
      };
    }

    const routed = await this.engineSourceRegistry.getActiveAdapter().routeEvent(eventId);
    if (routed) {
      this.context.eventBus.emit(EBusInternalEventTypes.EVENT_ROUTED, { eventId });
    }

    return {
      success: false,
      routeId: null,
      error:
        'Routing Engine Not Active — Cannot route events until real event sources are connected.',
      isPlaceholder: true,
      source: 'Event Bus — Routing Engine Not Active',
    };
  }

  async registerRoute(
    _sourceModule: string,
    _destinationModule: string,
    _policy: RTWORoutingPolicy
  ): Promise<RoutingResult> {
    return {
      success: false,
      routeId: null,
      error:
        'Routing Engine Not Active — Cannot register routes until real module connections are established.',
      isPlaceholder: true,
      source: 'Event Bus — Routing Engine Not Active',
    };
  }

  getAllRoutes(): RTWOEventRoute[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.routes.findAll();
  }

  getRoute(routeId: RouteId): RTWOEventRoute | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.routes.findById(routeId);
  }

  getRoutesByStatus(_status: RTWORouteStatus): RTWOEventRoute[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return [];
  }
}
